import { useCallback } from 'react';

import { usePico } from '../pico';
import { type PaywallTrackingProps } from './use-paywall-tracking';

export type UpgradeButtonClickedProps = {
  name: string;
  location: string;
  targetUrl?: string;
};

/**
 * Hook for tracking upgrade button clicks.
 * Should be used when any upgrade button is clicked.
 */
export const useUpgradeTracking = () => {
  const pico = usePico();

  const trackUpgradeButtonClicked = useCallback(
    (props: UpgradeButtonClickedProps) => {
      if (pico === null) {
        return;
      }
      pico.track('upgrade_button_clicked', {
        button_name: props.name,
        button_location: props.location,
        target_url: props.targetUrl ?? null,
        page_url: typeof window !== 'undefined' ? window.location.href : null,
      });
    },
    [pico],
  );

  return {
    trackUpgradeButtonClicked,
  };
};

/**
 * Props for buildUpgradePlanUrl - paywallType is optional and defaults to 'page'
 */
export type BuildUpgradePlanUrlProps = Omit<PaywallTrackingProps, 'paywallType'>;

/**
 * Builds a URL to /upgrade-plan with paywall tracking parameters.
 * These parameters will be read by the upgrade-plan page to track `paywall_displayed`.
 */
export const buildUpgradePlanUrl = (
  paywallTracking: BuildUpgradePlanUrlProps,
  additionalParams?: Record<string, string>,
): string => {
  const params = new URLSearchParams({
    paywall_trigger: paywallTracking.paywallTrigger,
    paywall_location: paywallTracking.paywallLocation,
    paywall_type: 'page',
    paywall_feature: paywallTracking.paywallFeature,
    ...additionalParams,
  });

  return `/upgrade-plan?${params.toString()}`;
};
